// Linha da listagem: descrição, id copiável, white label, status e o resumo das
// regras (vencimento, multa, juros) — o suficiente para escolher sem abrir.
import { ChevronRightIcon, PowerIcon } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CopyableValue } from "@/components/commons/copyable-value"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { PaymentConfiguration } from "@/types/payment-configurations"

import { CARD_ACCENT_CLASSES } from "../constants"
import { ChargeRuleText } from "./charge-rule-text"
import { PaymentConfigurationStatusChip } from "./payment-configuration-status-chip"

const prefix = "pages.paymentConfigurations"

interface PaymentConfigurationCardProps {
  configuration: PaymentConfiguration
  whiteLabelName: string
  detailHref: string
  canWrite: boolean
  isToggling: boolean
  onActivate: (configuration: PaymentConfiguration) => void
  onDeactivate: (
    configuration: PaymentConfiguration
  ) => void
}

interface RuleItemProps {
  label: string
  children: React.ReactNode
}

const RuleItem = ({ label, children }: RuleItemProps) => (
  <div className="min-w-0 space-y-0.5">
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </dt>
    <dd className="text-sm">{children}</dd>
  </div>
)

export const PaymentConfigurationCard = memo(
  ({
    configuration,
    whiteLabelName,
    detailHref,
    canWrite,
    isToggling,
    onActivate,
    onDeactivate,
  }: PaymentConfigurationCardProps) => {
    const { t } = useTranslation()
    const title =
      configuration.description || t(`${prefix}.untitled`)

    return (
      <article
        aria-label={title}
        className={cn(
          "overflow-hidden rounded-xl border border-l-4 bg-card shadow-sm transition-shadow hover:shadow-md",
          configuration.active
            ? CARD_ACCENT_CLASSES.active
            : CARD_ACCENT_CLASSES.inactive
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-sm font-semibold">
                {title}
              </h2>
              <PaymentConfigurationStatusChip
                active={configuration.active}
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <CopyableValue
                value={configuration.id}
                label={t(`${prefix}.fields.id`)}
              />
              <span>{whiteLabelName}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canWrite &&
              (configuration.active ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  disabled={isToggling}
                  aria-busy={isToggling}
                  onClick={() =>
                    onDeactivate(configuration)
                  }
                >
                  <PowerIcon
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  {t(`${prefix}.actions.deactivate`)}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  disabled={isToggling}
                  aria-busy={isToggling}
                  onClick={() => onActivate(configuration)}
                >
                  <PowerIcon
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  {t(`${prefix}.actions.activate`)}
                </Button>
              ))}
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-8 gap-1"
            >
              <Link
                to={detailHref}
                aria-label={t(
                  `${prefix}.actions.viewDetailsOf`,
                  {
                    name: title,
                  }
                )}
              >
                {t(`${prefix}.actions.viewDetails`)}
                <ChevronRightIcon
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                />
              </Link>
            </Button>
          </div>
        </div>
        <dl className="grid gap-4 border-t px-5 py-3 sm:grid-cols-3">
          <RuleItem label={t(`${prefix}.fields.daysDue`)}>
            <span className="tabular-nums">
              {t(`${prefix}.rules.days`, {
                count: configuration.daysDue ?? 0,
              })}
            </span>
          </RuleItem>
          <RuleItem label={t(`${prefix}.fields.fine`)}>
            <ChargeRuleText charge={configuration.fine} />
          </RuleItem>
          <RuleItem label={t(`${prefix}.fields.interest`)}>
            <ChargeRuleText
              charge={configuration.interest}
            />
          </RuleItem>
        </dl>
      </article>
    )
  }
)
PaymentConfigurationCard.displayName =
  "PaymentConfigurationCard"
